import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, ArrowUpRight, Mail } from "lucide-react";
import { Logo } from "./Logo";
import { SERVICES } from "../data/services";
import { BIZ, CITIES, track } from "../data/site";

export const Footer = () => (
  <footer id="location" data-testid="contact-footer" className="relative border-t border-white/[0.07] surface-panel overflow-hidden noise-overlay">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
        <div className="lg:col-span-4">
          <Link to="/" aria-label="Westway Collision Center — home">
            <Logo variant="footer" />
          </Link>
          <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-xs">
            Certified collision repair, frame straightening, and paintless dent repair for luxury
            and exotic vehicles — backed by a written lifetime warranty.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <a
              href={BIZ.phoneHref}
              data-testid="footer-phone-link"
              onClick={() => track("click_to_call", { location: "footer" })}
              className="inline-flex items-center gap-2.5 text-slate-200 hover:text-accent transition-colors text-sm"
            >
              <Phone size={15} className="text-accent" /> {BIZ.phoneDisplay}
            </a>
            <a href={`mailto:${BIZ.email}`} data-testid="footer-email-link" className="inline-flex items-center gap-2.5 text-slate-200 hover:text-accent transition-colors text-sm">
              <Mail size={15} className="text-accent" /> {BIZ.email}
            </a>
            <a
              href={BIZ.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-address-link"
              className="inline-flex items-start gap-2.5 text-slate-200 hover:text-accent transition-colors text-sm"
            >
              <MapPin size={15} className="text-accent mt-0.5 shrink-0" />
              <span>{BIZ.street}<br />{BIZ.city}, {BIZ.state} {BIZ.zip}</span>
            </a>
            <p className="inline-flex items-start gap-2.5 text-sm text-slate-400">
              <Clock size={15} className="text-accent mt-0.5 shrink-0" />
              <span>Mon–Fri 7:30 AM – 6:00 PM<br />Sat 8:00 AM – 5:00 PM · Sun Closed</span>
            </p>
          </div>
        </div>

        <nav className="lg:col-span-3" aria-label="Footer services">
          <h3 className="font-mono text-[10px] tracking-[0.28em] uppercase text-accent/90">Services</h3>
          <ul className="mt-5 grid grid-cols-1 gap-2.5">
            {SERVICES.slice(0, 8).map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="text-sm text-slate-400 hover:text-accent transition-colors">{s.menuName}</Link>
              </li>
            ))}
            <li>
              <Link to="/services" className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-accentdeep transition-colors">
                All 15 services <ArrowUpRight size={13} />
              </Link>
            </li>
          </ul>
        </nav>

        <nav className="lg:col-span-2" aria-label="Footer company">
          <h3 className="font-mono text-[10px] tracking-[0.28em] uppercase text-accent/90">Company</h3>
          <ul className="mt-5 grid grid-cols-1 gap-2.5">
            {[
              ["About Us", "/about"],
              ["Our Work", "/our-work"],
              ["Performance & DME", "/dme-tuning-dallas"],
              ["Free Estimate", "/free-estimate"],
              ["We Accept", "/what-we-accept"],
              ["FAQ", "/faq"],
              ["Contact", "/contact"],
            ].map(([label, to]) => (
              <li key={to}>
                <Link to={to} className="text-sm text-slate-400 hover:text-accent transition-colors">{label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav className="lg:col-span-3" aria-label="Footer service areas">
          <h3 className="font-mono text-[10px] tracking-[0.28em] uppercase text-accent/90">Service Areas</h3>
          <ul className="mt-5 grid grid-cols-2 gap-2.5">
            {CITIES.map((c) => (
              <li key={c.slug}>
                <Link to={`/service-areas/${c.slug}`} className="text-sm text-slate-400 hover:text-accent transition-colors">
                  {c.name}, TX
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>

    <div className="border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7 flex flex-col sm:flex-row flex-wrap items-center justify-between gap-x-6 gap-y-4 pb-24 md:pb-7">
        <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-slate-500 text-center">
          &copy; 2026 {BIZ.name} — Carrollton, TX — DFW Metroplex
        </p>
        <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-slate-600 text-center">
          Hero photo:{" "}
          <a href="https://unsplash.com/photos/JMpdblrHTrQ" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">
            Bailey Mahon / Unsplash
          </a>
        </p>
        <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-accent/70">
          I-CAR Gold Class Facility · Written Lifetime Warranty
        </p>
      </div>
    </div>
  </footer>
);
